
import { Sale } from "@/models/base";
import { CalendarEvent, GetColorMethod } from "@/models/calendar-event";
import { SaleService } from "@/services/sale-service";
import { printLocalDate } from "@/utils/pretty-print";
import { requestNotificationPermission } from "@/utils/notifications";
import Vue from "vue";
import AppDateTimeEdit from "./DateTimeEdit.vue";
import AppSaleSearch from "@/components/sales/SaleSearchWithBrowse.vue";
import AppCalendarSelect from "@/components/calendar/CalendarSelect.vue";
import AppCalendarEventReminders from "@/components/calendar/CalendarEventReminders.vue";
import AppColorPicker from "@/components/ColorPicker.vue";
import { ISelectItem } from "winfakt-vue-components";
import { getEnumKeys, getEnumValueFromKey } from "@/utils/enum";

export default Vue.extend({
	data() {
		return {
			sale: null as null | Sale,
			// Zoekveld voor een verkoopdocument tonen (standaard open bij een nieuw item zonder koppeling)
			linkingSale: false
		};
	},
	props: {
		event: {
			type: Object as () => CalendarEvent,
			required: true
		},
	},
	computed: {
		getColorMethodItems():ISelectItem<GetColorMethod>[] {
			return getEnumKeys(GetColorMethod).map(k=>{
				return {
					text: this.$ct(`get-color-method-short.${k}`),
					value: getEnumValueFromKey(GetColorMethod, k)
				} as ISelectItem<GetColorMethod>;
			});
		},
		showColorPicker():boolean {
			return this.event.GetColorMethod == GetColorMethod.COLOR_FIELD;
		},
		saleSubtitle():string {
			if (!this.sale) return "";
			let parts = [SaleService.getTypeName(this.sale.TypeID), printLocalDate(this.sale.InvoiceDate)];
			if (this.sale.DeliveryDate) {
				parts.push(this.$ct("link.delivery", {date: printLocalDate(this.sale.DeliveryDate)}).toString());
			}
			return parts.filter(p => p).join(" · ");
		},
		crmSubtitle():string {
			let crm = this.event.Crm;
			if (!crm) return "";
			let parts = [] as string[];
			if (crm.Contact) parts.push(crm.Contact.getDisplayName());
			if (crm.TimeSheduled) parts.push(this.$ct("link.scheduled", {date: printLocalDate(crm.TimeSheduled)}).toString());
			return parts.filter(p => p).join(" · ");
		},
		endTimeHasError():boolean {
			return this.event.StartTime.getTime() > this.event.EndTime.getTime();
		},
		durationText():string {
			let start = this.event.StartTime;
			let end = this.event.EndTime;
			if (this.event.AllDay) {
				// Een hele-dag item eindigt op dezelfde dag als het begint (zie calendar-state), dus tellen we de dagen inclusief.
				let startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
				let endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
				let days = Math.max(1, Math.round((endDay.getTime() - startDay.getTime()) / 86400000) + 1);
				if (days == 1) return this.$ct("duration.day").toString();
				return this.$ct("duration.days", {days}).toString();
			}
			let minutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
			let hours = Math.floor(minutes / 60);
			minutes = minutes % 60;
			if (hours == 0) return this.$ct("duration.minutes", {minutes}).toString();
			if (minutes == 0) return this.$ct("duration.hours", {hours}).toString();
			return this.$ct("duration.hours-minutes", {hours, minutes: minutes.toString().padStart(2, "0")}).toString();
		}
	},
	watch: {
		sale(){
			if (this.sale) {
				this.linkingSale = false;
			}
			if (!this.sale){
				this.event.SaleID = 0;
				this.event.SaleBookYear = 0;
				return;
			}
			this.event.SaleID = this.sale.ID;
			this.event.SaleBookYear = this.sale.BookYear;
		}
	},
	methods: {
		addReminder() {
			this.event.addReminder();
			requestNotificationPermission();
		},
		openSale(){
			if (!this.sale) return;
			if (!window.location) return;
			let resultWindow = window.open(`/sales/info/${this.sale.ID}`, "_blank");
			if (!resultWindow) return;
			resultWindow.focus();
		},
		openCrm(){
			if (!this.event.CrmID) return;
			if (!window.location) return;
			let resultWindow = window.open(`/crm/edit/${this.event.CrmID}`, "_blank");
			if (!resultWindow) return;
			resultWindow.focus();
		}
	},
	async mounted() {
		if (this.event.SaleID) {
			this.sale = await SaleService.getSale(this.event.SaleID, this.event.SaleBookYear);
		} else if (this.event.ID == 0 && !this.event.CrmID) {
			this.linkingSale = true;
		}
	},
	components: {
		AppDateTimeEdit,
		AppCalendarEventReminders,
		AppSaleSearch,
		AppCalendarSelect,
		AppColorPicker
	},
	i18nEx: {
		componentPrefix: "components.calendar"
	}
});
